import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Settings, Smartphone } from 'lucide-react';
import config from '../../config';
import { useSelectedMetrics, isMetricSelected, getCosClassName, getCpaClassName, useComponentMetrics, getMetricObjectiveClassName, getMetricLabel, libelleConversions, grapheSurTransactions, libelleTauxConversion, infobulleEnteteConversion } from '../../utils/metricsUtils';
import MetricsConfigModal from '../MetricsConfigModal';
import EnteteAvecBulle from '../EnteteAvecBulle';
import ModuleVisibilityToggle from '../ModuleVisibilityToggle';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

import { calculateVariation, renderVariation } from '../../utils/variationUtils';
import { estErreurAcces } from './accesMeta';
import { getDateRangeFromPeriod, calculatePreviousPeriod as calculateComparisonPeriod } from '../../utils/periodUtils';
const API_URL = config.API_URL;

// Palette de couleurs pour les graphiques (même que Google Ads)
const COLORS = ['#0088FE', '#82ca9d', '#FFBB28', '#FF8042', '#8884d8'];

/**
 * Composant table modulaire des performances par appareil Facebook Ads
 * Optimisé avec React.memo et useMemo pour les performances
 */
const DeviceTable = React.memo(({
  token,
  selectedAccount,
  period,
  customStartDate,
  customEndDate,
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedAccountProp = null,
  selectedCampaignFilter = null,
  selectedPlatformFilter = null,
  labelCampaignIds = null,
  compareEnabled = false,
  comparisonType = '',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'fbads_devices',
  // Le total dédupliqué du compte : voir `renderTotalCell`.
  totalCouverture = null,
  totalRepetition = null,
  /**
   * La définition de la métrique Conversions sur ce compte, transmise par `App.js`.
   *
   * Ce tableau interroge sa propre route et n'a donc pas cette information : c'est la timeline
   * du dashboard qui la porte, et elle descend en propriété — comme la couverture dédupliquée.
   * `null` signifie « rien de configuré », et la métrique compte alors les achats.
   */
  conversionConfig = null
}) => {
  const [data, setData] = useState([]);
  const [previousData, setPreviousData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Récupérer les métriques sélectionnées
  const selectedMetrics = useSelectedMetrics();

  // Hook pour les métriques du composant
  const componentMetrics = useComponentMetrics('fbads_devices', selectedAccountProp?.id || selectedAccount || 'global');

  // Fonction helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Le template « Engagement » remplace les conversions par les interactions dans les
  // métriques du composant. Le graphique suit ce choix — titre, série et données —
  // plutôt que d'annoncer des interactions en traçant des conversions.
  const showInteractions = isComponentMetricActive('interactions') && !isComponentMetricActive('conv');

  // Helper pour obtenir le label d'une métrique (avec labels personnalisés)
  const getLabel = useCallback((metricKey) => {
    /*
     * ⚠️ **`conv` et `taux_conv` ne se nomment pas dans une table figée.**
     *
     * Leur intitulé dépend de ce que l'utilisateur a choisi de compter : « Conversions (Ajout
     * au panier) » et non « Conversions ». Un nom saisi à la main dans la modale l'emporte
     * toujours, et c'est `getMetricLabel` qui le détient — d'où l'ordre : on lui demande
     * d'abord, et on ne calcule le nom que s'il n'a rien de personnalisé à dire.
     */
    const personnalise = getMetricLabel(metricKey, moduleId, selectedAccountProp?.id || selectedAccount || 'global');
    if (metricKey === 'conv') return libelleConversions(conversionConfig, null, 'fbads') || personnalise;
    if (metricKey === 'taux_conv') return libelleTauxConversion(conversionConfig, null, 'fbads') || personnalise;
    return personnalise;
  }, [moduleId, selectedAccountProp?.id, selectedAccount, conversionConfig]);

  // Fonctions de formatage locales
  const formatNumberLocal = (num) => new Intl.NumberFormat('fr-FR').format(Math.round(num || 0));
  const formatCurrency = (num) => `€${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0)}`;
  const formatPercentage = (num) => `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0)}%`;

  // Configuration des métriques pour le rendu dynamique
  const metricConfig = useMemo(() => ({
    impressions: { sortKey: 'impressions', defaultLabel: 'Impressions' },
    couverture: { sortKey: 'reach', defaultLabel: 'Couverture' },
    repetition: { sortKey: 'frequency', defaultLabel: 'Répétition' },
    ctr: { sortKey: 'ctr', defaultLabel: 'CTR' },
    clics: { sortKey: 'clicks', defaultLabel: 'Clics' },
    cpc: { sortKey: 'cpc', defaultLabel: 'CPC' },
    cout: { sortKey: 'cost', defaultLabel: 'Coût' },
    taux_conv: { sortKey: 'conversionRate', defaultLabel: 'Taux conv.' },
    conv: { sortKey: 'conversions', defaultLabel: 'Conv.' },
    cpa: { sortKey: 'cpa', defaultLabel: 'CPA' },
    valeur_conv: { sortKey: 'conversionsValue', defaultLabel: 'Valeur Conv.' },
    /**
     * Les achats, distincts des conversions.
     *
     * `conv` compte ce que l'utilisateur a configuré ; `transactions` compte les achats, et
     * seulement eux. Sur un compte dont les conversions configurées *sont* les achats, les
     * deux colonnes affichent le même nombre — c'est pour cela que celle-ci est décochée par
     * défaut, et non parce qu'elle serait secondaire.
     */
    transactions: { sortKey: 'transactions', defaultLabel: 'Transactions' },
    cos: { sortKey: 'roas', defaultLabel: 'COS' },
    interactions: { sortKey: 'interactions', defaultLabel: 'Interactions' },
    cout_par_interaction: { sortKey: 'cout_par_interaction', defaultLabel: 'Coût/Interaction' },
    reactions: { sortKey: 'reactions', defaultLabel: 'Réactions' },
    cout_par_reaction: { sortKey: 'cout_par_reaction', defaultLabel: 'Coût/Réaction' },
    commentaires: { sortKey: 'comments', defaultLabel: "Commentaires" },
    enregistrements: { sortKey: 'saves', defaultLabel: "Enregistrements" },
    partages: { sortKey: 'shares', defaultLabel: "Partages" },
    jaime_facebook: { sortKey: 'pageLikes', defaultLabel: "J'aime (Facebook)" },
  }), []);

  // Fonction pour rendre une cellule selon la métrique
  const renderMetricCell = useCallback((metricKey, item, prevItem, cpa, cos, coutParInteraction, coutParReaction, prevCpa, prevCos, prevCoutParInteraction, prevCoutParReaction) => {
    const accountId = selectedAccountProp?.id || selectedAccount || 'global';
    switch (metricKey) {
      case 'impressions':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.impressions || 0)}
            {prevItem && renderVariation(calculateVariation(item.impressions || 0, prevItem.impressions || 0))}
          </td>
        );
      /**
       * Couverture et répétition, par segment.
       *
       * Meta déduplique la couverture **à l'intérieur de chaque segment** : la valeur d'une
       * ligne est donc juste. Leur somme ne l'est pas — une personne vue sur deux segments y
       * figure deux fois —, d'où le tiret sur la ligne de total (voir `renderTotalCell`).
       */
      case 'couverture':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.reach || 0)}
            {prevItem && renderVariation(calculateVariation(item.reach || 0, prevItem.reach || 0))}
          </td>
        );
      case 'repetition':
        return (
          <td key={metricKey} className="numeric">
            {(item.frequency || 0).toFixed(2)}
            {prevItem && renderVariation(calculateVariation(item.frequency || 0, prevItem.frequency || 0))}
          </td>
        );
      case 'ctr':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('ctr', item.ctr, moduleId, accountId)}`}>
            {formatPercentage(item.ctr || 0)}
            {prevItem && renderVariation(calculateVariation(item.ctr || 0, prevItem.ctr || 0))}
          </td>
        );
      case 'clics':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.clicks || 0)}
            {prevItem && renderVariation(calculateVariation(item.clicks || 0, prevItem.clicks || 0))}
          </td>
        );
      case 'cpc':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('cpc', item.cpc, moduleId, accountId)}`}>
            {formatCurrency(item.cpc || 0)}
            {prevItem && renderVariation(calculateVariation(item.cpc || 0, prevItem.cpc || 0), true)}
          </td>
        );
      case 'cout':
        return (
          <td key={metricKey} className="numeric">
            {formatCurrency(item.cost || 0)}
            {prevItem && renderVariation(calculateVariation(item.cost || 0, prevItem.cost || 0), true)}
          </td>
        );
      case 'taux_conv':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('taux_conv', item.conversionRate, moduleId, accountId)}`}>
            {formatPercentage(item.conversionRate || 0)}
            {prevItem && renderVariation(calculateVariation(item.conversionRate || 0, prevItem.conversionRate || 0))}
          </td>
        );
      case 'conv':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.conversions || 0)}
            {prevItem && renderVariation(calculateVariation(item.conversions || 0, prevItem.conversions || 0))}
          </td>
        );
      case 'transactions':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.transactions || 0)}
            {prevItem && renderVariation(calculateVariation(item.transactions || 0, prevItem.transactions || 0))}
          </td>
        );
      case 'cpa':
        return (
          <td key={metricKey} className={`numeric ${getCpaClassName(cpa, accountId, moduleId)}`}>
            {cpa >= 0 ? formatCurrency(cpa) : '-'}
            {prevItem && renderVariation(calculateVariation(cpa, prevCpa), true)}
          </td>
        );
      case 'valeur_conv':
        return (
          <td key={metricKey} className="numeric">
            {formatCurrency(item.conversionsValue || 0)}
            {prevItem && renderVariation(calculateVariation(item.conversionsValue || 0, prevItem.conversionsValue || 0))}
          </td>
        );
      case 'cos':
        return (
          <td key={metricKey} className={`numeric ${getCosClassName(cos, accountId, moduleId)}`}>
            {formatNumber ? `${formatNumber(cos, 1)}%` : `${cos.toFixed(1)}%`}
            {prevItem && renderVariation(calculateVariation(cos, prevCos), true)}
          </td>
        );
      case 'interactions':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.interactions || 0)}
            {prevItem && renderVariation(calculateVariation(item.interactions || 0, prevItem.interactions || 0))}
          </td>
        );
      case 'cout_par_interaction':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('cout_par_interaction', coutParInteraction, moduleId, accountId)}`}>
            {coutParInteraction >= 0 ? formatCurrency(coutParInteraction) : '-'}
            {prevItem && renderVariation(calculateVariation(coutParInteraction, prevCoutParInteraction), true)}
          </td>
        );
      case 'reactions':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.reactions || 0)}
            {prevItem && renderVariation(calculateVariation(item.reactions || 0, prevItem.reactions || 0))}
          </td>
        );
      case 'commentaires':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.comments || 0)}
            {prevItem && renderVariation(calculateVariation(item.comments || 0, prevItem.comments || 0))}
          </td>
        );
      case 'enregistrements':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.saves || 0)}
            {prevItem && renderVariation(calculateVariation(item.saves || 0, prevItem.saves || 0))}
          </td>
        );
      case 'partages':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.shares || 0)}
            {prevItem && renderVariation(calculateVariation(item.shares || 0, prevItem.shares || 0))}
          </td>
        );
      case 'jaime_facebook':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.pageLikes || 0)}
            {prevItem && renderVariation(calculateVariation(item.pageLikes || 0, prevItem.pageLikes || 0))}
          </td>
        );
      case 'cout_par_reaction':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('cout_par_reaction', coutParReaction, moduleId, accountId)}`}>
            {coutParReaction >= 0 ? formatCurrency(coutParReaction) : '-'}
            {prevItem && renderVariation(calculateVariation(coutParReaction, prevCoutParReaction), true)}
          </td>
        );
      case 'roas': {
        const roas = item.cost > 0 ? (item.conversionsValue / item.cost) * 100 : 0;
        const prevRoas = prevItem && prevItem.cost > 0 ? (prevItem.conversionsValue / prevItem.cost) * 100 : 0;
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('roas', roas, moduleId, accountId)}`}>
            {roas >= 0 ? `${formatNumber ? formatNumber(roas, 0) : roas.toFixed(0)}%` : '-'}
            {prevItem && renderVariation(calculateVariation(roas, prevRoas))}
          </td>
        );
      }
      case 'roi': {
        const hasData = item.cost > 0 && item.conversionsValue > 0;
        const roi = hasData ? (item.conversionsValue - item.cost) / item.cost : 0;
        const prevHasData = prevItem && prevItem.cost > 0 && prevItem.conversionsValue > 0;
        const prevRoi = prevHasData ? (prevItem.conversionsValue - prevItem.cost) / prevItem.cost : 0;
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('roi', roi, moduleId, accountId)}`}>
            {(formatNumber ? formatNumber(roi, 2) : roi.toFixed(2))}
            {renderVariation(calculateVariation(roi, prevRoi))}
          </td>
        );
      }
      default:
        return null;
    }
  }, [selectedAccountProp?.id, selectedAccount, moduleId, formatNumber]);

  // Fonction pour rendre une cellule de total selon la métrique
  const renderTotalCell = useCallback((metricKey, totals, previousTotals) => {
    const accountId = selectedAccountProp?.id || selectedAccount || 'global';
    switch (metricKey) {
      case 'impressions':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.impressions)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
          </td>
        );
      /**
       * Pas de total pour les deux métriques de personnes.
       *
       * Additionner les couvertures de segments qui se recouvrent donnerait un nombre supérieur
       * à la réalité, et la répétition est un ratio bâti dessus. Le total dédupliqué du compte
       * existe, mais ailleurs : sur le tableau des campagnes et sur la carte « Couverture » des
       * résultats, qui portent sur l'ensemble du compte.
       */
      case 'couverture':
      case 'repetition': {
        /**
         * Le total vient du compte, jamais de la somme des lignes.
         *
         * Les segments se recouvrent : une personne vue sur deux d'entre eux figure dans les
         * deux lignes. Seul Meta sait déduplier, et il le fait pour le compte entier — c'est
         * cette valeur que le dashboard fait descendre. Elle manque dès qu'un filtre rétrécit
         * le tableau : un tiret vaut mieux qu'un chiffre portant sur un autre périmètre.
         */
        const valeurTotale = metricKey === 'couverture' ? totalCouverture : totalRepetition;
        return (
          <td
            key={metricKey}
            className="numeric"
            title={valeurTotale == null
              ? "Les segments se recouvrent : leur somme n'a pas de sens, et le total du compte ne correspond pas à un tableau filtré."
              : 'Couverture dédupliquée du compte, calculée par Meta sur la période'}
          >
            <strong>
              {valeurTotale == null
                ? '—'
                : (metricKey === 'couverture' ? formatNumberLocal(valeurTotale) : valeurTotale.toFixed(2))}
            </strong>
          </td>
        );
      }
      case 'ctr':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('ctr', totals.ctr, moduleId, accountId)}`}>
            <strong>{formatPercentage(totals.ctr)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.ctr, previousTotals.ctr))}
          </td>
        );
      case 'clics':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.clicks)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
          </td>
        );
      case 'cpc':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('cpc', totals.cpc, moduleId, accountId)}`}>
            <strong>{formatCurrency(totals.cpc)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.cpc, previousTotals.cpc), true)}
          </td>
        );
      case 'cout':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatCurrency(totals.cost)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
          </td>
        );
      case 'taux_conv':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('taux_conv', totals.conversionRate, moduleId, accountId)}`}>
            <strong>{formatPercentage(totals.conversionRate)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.conversionRate, previousTotals.conversionRate))}
          </td>
        );
      case 'conv':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.conversions)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
          </td>
        );
      case 'transactions':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.transactions || 0)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.transactions || 0, previousTotals.transactions || 0))}
          </td>
        );
      case 'cpa':
        return (
          <td key={metricKey} className={`numeric ${getCpaClassName(totals.cpa, accountId, moduleId)}`}>
            <strong>{totals.cpa >= 0 ? formatCurrency(totals.cpa) : '-'}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.cpa, previousTotals.cpa), true)}
          </td>
        );
      case 'valeur_conv':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatCurrency(totals.conversionsValue)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
          </td>
        );
      case 'cos':
        return (
          <td key={metricKey} className={`numeric ${getCosClassName(totals.cos, accountId, moduleId)}`}>
            <strong>{formatNumber ? `${formatNumber(totals.cos, 1)}%` : `${totals.cos.toFixed(1)}%`}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.cos, previousTotals.cos), true)}
          </td>
        );
      case 'interactions':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.interactions)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.interactions, previousTotals.interactions))}
          </td>
        );
      case 'cout_par_interaction':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('cout_par_interaction', totals.coutParInteraction, moduleId, accountId)}`}>
            <strong>{totals.coutParInteraction >= 0 ? formatCurrency(totals.coutParInteraction) : '-'}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.coutParInteraction, previousTotals.coutParInteraction), true)}
          </td>
        );
      case 'reactions':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.reactions)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.reactions, previousTotals.reactions))}
          </td>
        );
      case 'commentaires':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.comments || 0)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.comments || 0, previousTotals.comments || 0))}
          </td>
        );
      case 'enregistrements':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.saves || 0)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.saves || 0, previousTotals.saves || 0))}
          </td>
        );
      case 'partages':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.shares || 0)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.shares || 0, previousTotals.shares || 0))}
          </td>
        );
      case 'jaime_facebook':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.pageLikes || 0)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.pageLikes || 0, previousTotals.pageLikes || 0))}
          </td>
        );
      case 'cout_par_reaction':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('cout_par_reaction', totals.coutParReaction, moduleId, accountId)}`}>
            <strong>{totals.coutParReaction >= 0 ? formatCurrency(totals.coutParReaction) : '-'}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.coutParReaction, previousTotals.coutParReaction), true)}
          </td>
        );
      case 'roas': {
        const roas = totals.cost > 0 ? (totals.conversionsValue / totals.cost) * 100 : 0;
        const prevRoas = previousTotals && previousTotals.cost > 0 ? (previousTotals.conversionsValue / previousTotals.cost) * 100 : 0;
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('roas', roas, moduleId, accountId)}`}>
            <strong>{roas >= 0 ? `${formatNumber ? formatNumber(roas, 0) : roas.toFixed(0)}%` : '-'}</strong>
            {previousTotals && renderVariation(calculateVariation(roas, prevRoas))}
          </td>
        );
      }
      case 'roi': {
        const hasData = totals.cost > 0 && totals.conversionsValue > 0;
        const roi = hasData ? (totals.conversionsValue - totals.cost) / totals.cost : 0;
        const prevHasData = previousTotals && previousTotals.cost > 0 && previousTotals.conversionsValue > 0;
        const prevRoi = prevHasData ? (previousTotals.conversionsValue - previousTotals.cost) / previousTotals.cost : 0;
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('roi', roi, moduleId, accountId)}`}>
            <strong>{(formatNumber ? formatNumber(roi, 2) : roi.toFixed(2))}</strong>
            {renderVariation(calculateVariation(roi, prevRoi))}
          </td>
        );
      }
      default:
        return null;
    }
  // `totalCouverture` et `totalRepetition` font partie des dépendances : sans elles, le
  // rappel mémoïsé garderait le total du compte précédent après un changement de compte
  // ou de période.
  }, [selectedAccountProp?.id, selectedAccount, moduleId, formatNumber, totalCouverture, totalRepetition]);





  const fetchDeviceData = useCallback(async (currentLabelCampaignIds) => {
    console.log('📱 [DEVICE-DEBUG] selectedAccount:', selectedAccount);
    console.log('📱 [DEVICE-DEBUG] token:', token ? `Token présent (${token.length} chars)` : 'Token manquant');
    console.log('📱 [DEVICE-DEBUG] currentLabelCampaignIds:', currentLabelCampaignIds);

    if (!selectedAccount || !token) {
      console.log('📱 [DEVICE-DEBUG] Conditions non remplies - selectedAccount:', !!selectedAccount, 'token:', !!token);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('📱 [DEVICE-DEBUG] Appel API POST vers:', `${API_URL}/api/facebook-ads/device`);
      const requestBody = {
        accountId: selectedAccount,
        period,
        customStartDate,
        customEndDate,
        ...(selectedCampaignFilter && { campaignFilter: selectedCampaignFilter }),
        ...(selectedPlatformFilter && { platformFilter: selectedPlatformFilter }),
        ...(currentLabelCampaignIds && { labelCampaignIds: currentLabelCampaignIds })
      };

      console.log('📱 [DEVICE-DEBUG] Body:', requestBody);
      console.log('📱 [DEVICE-DEBUG] Headers:', { Authorization: `Bearer ${token.substring(0, 10)}...` });

      const response = await axios.post(
        `${API_URL}/api/facebook-ads/device`,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setData(response.data.data || []);

        // Récupérer les données de la période précédente si comparaison activée
        if (compareEnabled && comparisonType) {
          let currentStart = customStartDate;
          let currentEnd = customEndDate;
          if (!currentStart || !currentEnd) {
            const dateRange = getDateRangeFromPeriod(period);
            currentStart = dateRange.startDate;
            currentEnd = dateRange.endDate;
          }
          const comparisonDates = calculateComparisonPeriod(currentStart, currentEnd, comparisonType);
          console.log('📱 [DEVICE-COMPARE] Récupération période précédente:', comparisonDates);

          const prevResponse = await axios.post(
            `${API_URL}/api/facebook-ads/device`,
            { ...requestBody, period: 'CUSTOM', customStartDate: comparisonDates.startDate, customEndDate: comparisonDates.endDate },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (prevResponse.data.success) {
            setPreviousData(prevResponse.data.data || []);
          }
        } else {
          setPreviousData([]);
        }
      } else {
        setError(response.data.error || 'Erreur lors du chargement des données');
      }
    } catch (err) {
      console.error('📱 [DEVICE-DEBUG] Erreur complète:', err);
      console.error('📱 [DEVICE-DEBUG] Status:', err.response?.status);
      console.error('📱 [DEVICE-DEBUG] Data:', err.response?.data);
      console.error('📱 [DEVICE-DEBUG] Message:', err.message);

      let errorMessage = 'Erreur lors du chargement des données';

      if (err.response?.status === 401) {
        errorMessage = 'Erreur d\'authentification - veuillez vous reconnecter';
      } else if (err.response?.status === 404) {
        errorMessage = 'Endpoint non trouvé - vérifiez la configuration du serveur';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, period, customStartDate, customEndDate, token, selectedCampaignFilter, selectedPlatformFilter, compareEnabled, comparisonType]);

  useEffect(() => {
    if (selectedAccount && token) {
      fetchDeviceData(labelCampaignIds);
    }
  }, [selectedAccount, period, customStartDate, customEndDate, token, selectedCampaignFilter, selectedPlatformFilter, labelCampaignIds, fetchDeviceData, compareEnabled, comparisonType]);

  // Fonction de tri memoized
  const sortedData = useMemo(() => {
    if (!data.length) return [];

    const sorted = [...data];
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      // Calcul spécial pour CPA
      if (sortConfig.key === 'cpa') {
        aValue = a.conversions > 0 ? (a.cost / a.conversions) : Number.MAX_VALUE;
        bValue = b.conversions > 0 ? (b.cost / b.conversions) : Number.MAX_VALUE;
      } else {
        aValue = a[sortConfig.key] || 0;
        bValue = b[sortConfig.key] || 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [data, sortConfig]);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!data.length) return {};

    const baseTotals = data.reduce((acc, item) => ({
      impressions: acc.impressions + (item.impressions || 0),
      clicks: acc.clicks + (item.clicks || 0),
      cost: acc.cost + (item.cost || 0),
      conversions: acc.conversions + (item.conversions || 0),
      transactions: acc.transactions + (item.transactions || 0),
      conversionsValue: acc.conversionsValue + (item.conversionsValue || 0),
      interactions: acc.interactions + (item.interactions || 0),
      reactions: acc.reactions + (item.reactions || 0),
      comments: acc.comments + (item.comments || 0),
      saves: acc.saves + (item.saves || 0),
      shares: acc.shares + (item.shares || 0),
      pageLikes: acc.pageLikes + (item.pageLikes || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, transactions: 0, interactions: 0, reactions: 0, comments: 0, saves: 0, shares: 0, pageLikes: 0 });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0,
      cout_par_interaction: baseTotals.interactions > 0 ? (baseTotals.cost / baseTotals.interactions) : 0,
      cout_par_reaction: baseTotals.reactions > 0 ? (baseTotals.cost / baseTotals.reactions) : 0
    };
  }, [data]);

  // Calcul des totaux précédents memoized
  const previousTotals = useMemo(() => {
    if (!previousData.length) return null;

    const baseTotals = previousData.reduce((acc, item) => ({
      impressions: acc.impressions + (item.impressions || 0),
      clicks: acc.clicks + (item.clicks || 0),
      cost: acc.cost + (item.cost || 0),
      conversions: acc.conversions + (item.conversions || 0),
      transactions: acc.transactions + (item.transactions || 0),
      conversionsValue: acc.conversionsValue + (item.conversionsValue || 0),
      interactions: acc.interactions + (item.interactions || 0),
      reactions: acc.reactions + (item.reactions || 0),
      comments: acc.comments + (item.comments || 0),
      saves: acc.saves + (item.saves || 0),
      shares: acc.shares + (item.shares || 0),
      pageLikes: acc.pageLikes + (item.pageLikes || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, transactions: 0, interactions: 0, reactions: 0, comments: 0, saves: 0, shares: 0, pageLikes: 0 });

    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0,
      cout_par_interaction: baseTotals.interactions > 0 ? (baseTotals.cost / baseTotals.interactions) : 0,
      cout_par_reaction: baseTotals.reactions > 0 ? (baseTotals.cost / baseTotals.reactions) : 0
    };
  }, [previousData]);

  /**
   * Le nom d'un appareil, en français.
   *
   * ## Les clés attendues n'étaient pas celles que Meta envoie
   *
   * Cette table visait `mobile`, `tablet`, `mobile_web` — un vocabulaire que l'API ne rend pas.
   * Le breakdown employé ici est `impression_device`, dont les valeurs sont
   * `android_smartphone`, `android_tablet`, `iphone`, `ipad`, `ipod`, `desktop` et `other`.
   * Seul `desktop` tombait juste ; tout le reste s'affichait tel quel, tirets bas compris.
   *
   * Les anciennes clés sont conservées : d'autres découpages de Meta les emploient, et une
   * table qui perd des entrées en gagnant des voisines ne fait que déplacer le problème.
   *
   * ## Le repli remet en forme plutôt que de recopier
   *
   * Meta ajoute des appareils sans prévenir. Une valeur inconnue est donc nettoyée — tirets bas
   * en espaces, initiale en capitale — pour qu'elle reste lisible en attendant d'être traduite,
   * au lieu de livrer un identifiant technique dans une colonne de rapport.
   */
  const formatDeviceName = (device) => {
    const deviceNames = {
      // `impression_device`, ce que renvoie réellement ce tableau
      'android_smartphone': 'Smartphone Android',
      'android_tablet': 'Tablette Android',
      'iphone': 'iPhone',
      'ipad': 'iPad',
      'ipod': 'iPod',
      'desktop': 'Ordinateur',
      'other': 'Autre',

      // Vocabulaire des autres découpages, conservé
      'mobile': 'Mobile',
      'mobile_app': 'Application mobile',
      'mobile_web': 'Mobile',
      'tablet': 'Tablette',
      'connected_tv': 'TV connectée',
      'unknown': 'Autre'
    };

    const cle = String(device ?? '').toLowerCase();
    if (deviceNames[cle]) return deviceNames[cle];
    if (!cle) return 'Autre';

    return cle.split('_').join(' ').replace(/^./, c => c.toUpperCase());
  };

  // Fonction pour obtenir la couleur d'un appareil
  const getDeviceColor = (device, index) => {
    return COLORS[index % COLORS.length];
  };

  // Données pour les graphiques en secteurs
  const clicksChartData = useMemo(() => {
    return data.map(device => ({
      name: formatDeviceName(device.device || 'unknown'),
      value: device.clicks || 0,
      percentage: totals.clicks > 0 ? ((device.clicks / totals.clicks) * 100).toFixed(1) : 0
    })).filter(device => parseFloat(device.percentage) > 0.0);
  }, [data, totals.clicks]);

  /*
   * Le camembert de droite porte **une** mesure, et trois peuvent la revendiquer.
   *
   * L'ordre de priorité : les interactions si le bouton d'engagement est enfoncé, sinon les
   * transactions si elles sont cochées *à la place* des conversions, sinon les conversions.
   * `grapheSurTransactions` porte cette dernière règle, partagée avec les autres graphes de
   * répartition — Meta comme Google Analytics.
   */
  const surTransactions = grapheSurTransactions(isComponentMetricActive);
  const champDuGraphe = showInteractions ? 'interactions' : (surTransactions ? 'transactions' : 'conversions');
  const conversionsChartData = useMemo(() => {
    const total = totals[champDuGraphe] || 0;
    return data.map(device => {
      const value = device[champDuGraphe] || 0;
      return {
        name: formatDeviceName(device.device || 'unknown'),
        value,
        percentage: total > 0 ? ((value / total) * 100).toFixed(1) : 0
      };
    }).filter(device => parseFloat(device.percentage) > 0.0);
  }, [data, totals, champDuGraphe]);

  /** Le titre du camembert, qui doit nommer exactement ce qu'il montre. */
  const titreDuGraphe = showInteractions
    ? 'Interactions par appareil'
    : (surTransactions
      ? 'Transactions par appareil'
      : `${libelleConversions(conversionConfig, null, 'fbads')} par appareil`);

  // Données pour la légende partagée
  const legendData = useMemo(() => {
    return data.map((device, index) => ({
      value: formatDeviceName(device.device || 'unknown'),
      type: 'rect',
      color: COLORS[index % COLORS.length]
    }));
  }, [data]);

  // Configuration de pagination
  const itemsPerPage = 20;
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Réinitialiser la page quand les données changent
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Données paginées memoized
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, itemsPerPage, totalItems]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  }, [sortConfig]);

  // Handler de pagination memoized
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, newPage)));
  }, [totalPages]);

  // Ne pas rendre si pas de données et pas de loading/erreur
  if (!loading && !error && !data.length) return null;

  // Vérification de visibilité pour les clients
  /**
   * Accès Meta expiré : la section disparaît, le bandeau de tête suffit.
   *
   * Elle affichait sinon le code brut du serveur — « ❌ auth_expired » — sous son titre, alors
   * que l'écran porte déjà, une fois, l'avertissement et le bouton qui le résout. Un bloc vide
   * sous son intitulé se lit comme une panne de la section ; c'est la connexion qui est en
   * cause, et elle est annoncée là où on peut agir.
   */
  if (estErreurAcces(error)) return null;

  const clientModuleVisible = isClientModuleVisible ? isClientModuleVisible(moduleId) : true;
  if (userRole === 'CLIENT' && !clientModuleVisible) {
    return null;
  }

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Smartphone size={16} />&nbsp;Appareils</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={clientModuleVisible}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Appareils"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {(userRole !== 'CLIENT' || clientModuleVisible) && isVisible && (
        <>
          {loading && (
            <div className="loading-container">
              <div className="spinner"></div>
              <span>Chargement des données par appareil...</span>
            </div>
          )}

          {error && (
            <div className="error-container">
              <span>❌ {error}</span>
            </div>
          )}

          {!loading && !error && data.length === 0 && (
            <div className="no-data-container">
              <span>Aucune donnée disponible pour cette période</span>
            </div>
          )}

          {!loading && !error && data.length > 0 && (
            <>
              {/* Section Graphiques - Graphiques en secteurs pour les performances par appareil */}
              <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px' }}>
                      
                      {/* Graphique Clics */}
                      <div style={{ flex: 1, textAlign: 'center' }}>
                        <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600' }}>
                          Clics par Appareil
                        </h4>
                        <div style={{ height: '280px' }}>
                          {clicksChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280} minWidth={100} minHeight={100}>
                              <PieChart>
                                <Pie
                                  data={clicksChartData}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ percentage }) => `${percentage}%`}
                                  outerRadius={70}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {clicksChartData.map((entry, index) => (
                                    <Cell key={`clicks-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value, name) => [value + ' clics', name]} />
                              </PieChart>
                            </ResponsiveContainer>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '280px', color: '#666' }}>
                              Aucune donnée de clics
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Légende partagée */}
                      <div style={{ 
                        minWidth: '120px', 
                        padding: '0 20px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}>
                        {legendData.map((item, index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            marginBottom: '8px',
                            fontSize: '13px'
                          }}>
                            <div style={{ 
                              width: '12px', 
                              height: '12px', 
                              backgroundColor: item.color, 
                              marginRight: '8px',
                              borderRadius: '2px'
                            }}></div>
                            <span>{item.value}</span>
                          </div>
                        ))}
                      </div>

                      {/* Graphique Conversions */}
                      <div style={{ flex: 1, textAlign: 'center' }}>
                        <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600' }}>
                          {titreDuGraphe}
                        </h4>
                        <div style={{ height: '280px' }}>
                          {conversionsChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280} minWidth={100} minHeight={100}>
                              <PieChart>
                                <Pie
                                  data={conversionsChartData}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ percentage }) => `${percentage}%`}
                                  outerRadius={70}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {conversionsChartData.map((entry, index) => (
                                    <Cell key={`conversions-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value, name) => [`${value} ${showInteractions ? 'interactions' : (surTransactions ? 'transactions' : 'conversions')}`, name]} />
                              </PieChart>
                            </ResponsiveContainer>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '280px', color: '#666' }}>
                              {showInteractions ? "Aucune donnée d'interactions" : (surTransactions ? 'Aucune transaction' : 'Aucune donnée de conversions')}
                            </div>
                          )}
                        </div>
                      </div>

                </div>
              </div>

              <div className="campaigns-table-wrapper">
                <table
                  key={componentMetrics.metrics.join(',')}
                  className="campaigns-table"
                  style={{
                    width: '100%',
                    tableLayout: 'auto',
                    borderCollapse: 'collapse'
                  }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('device')} className="sortable">
                        Appareil {sortConfig.key === 'device' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      {/* Rendu dynamique des colonnes selon l'ordre configuré */}
                      {componentMetrics.order
                        .filter(key => isComponentMetricActive(key) && metricConfig[key])
                        .map(metricKey => {
                          const config = metricConfig[metricKey];
                          const label = getLabel(metricKey) || config.defaultLabel;
                          return (
                            <EnteteAvecBulle key={metricKey} onClick={() => handleSort(config.sortKey)} className="sortable numeric" texte={infobulleEnteteConversion(metricKey, conversionConfig)}>
                              {label} {sortConfig.key === config.sortKey && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                            </EnteteAvecBulle>
                          );
                        })}
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.map((item, index) => {
                      const cpa = item.conversions > 0 ? (item.cost / item.conversions) : 0;
                      const cos = item.conversionsValue > 0 ? (item.cost / item.conversionsValue * 100) : 0;
                      const coutParInteraction = item.interactions > 0 ? (item.cost / item.interactions) : 0;
                      const coutParReaction = item.reactions > 0 ? (item.cost / item.reactions) : 0;

                      // Trouver les données précédentes pour cet appareil
                      const prevItem = compareEnabled && previousData.length > 0
                        ? previousData.find(p => p.device === item.device)
                        : null;
                      const prevCpa = prevItem && prevItem.conversions > 0 ? (prevItem.cost / prevItem.conversions) : 0;
                      const prevCos = prevItem && prevItem.conversionsValue > 0 ? (prevItem.cost / prevItem.conversionsValue * 100) : 0;
                      const prevCoutParInteraction = prevItem && prevItem.interactions > 0 ? (prevItem.cost / prevItem.interactions) : 0;
                      const prevCoutParReaction = prevItem && prevItem.reactions > 0 ? (prevItem.cost / prevItem.reactions) : 0;

                      return (
                        <tr key={index}>
                          <td className="campaign-name">
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor: getDeviceColor(item.device, index),
                                marginRight: '8px',
                                flexShrink: 0
                              }}></div>
                              {formatDeviceName(item.device || 'unknown')}
                            </div>
                          </td>
                          {/* Rendu dynamique des cellules selon l'ordre configuré */}
                          {componentMetrics.order
                            .filter(key => isComponentMetricActive(key) && metricConfig[key])
                            .map(metricKey => renderMetricCell(metricKey, item, prevItem, cpa, cos, coutParInteraction, coutParReaction, prevCpa, prevCos, prevCoutParInteraction, prevCoutParReaction))}
                        </tr>
                      );
                    })}
                  </tbody>

                  <tfoot>
                    <tr className="totals-row">
                      <td className="campaign-name"><strong>TOTAL</strong></td>
                      {/* Rendu dynamique des totaux selon l'ordre configuré */}
                      {componentMetrics.order
                        .filter(key => isComponentMetricActive(key) && metricConfig[key])
                        .map(metricKey => renderTotalCell(metricKey, totals, previousTotals))}
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Contrôles de pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Affichage {startIndex + 1}-{Math.min(endIndex, sortedData.length)} sur {sortedData.length} appareils
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      ← Précédent
                    </button>
                    <span className="page-numbers">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button 
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Modal de configuration des métriques */}
      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId="fbads_devices"
          accountId={selectedAccountProp?.id || selectedAccount || 'global'}
          title="Appareils Meta Ads"
          hideApplyToAll={true}
          /*
           * La définition de la métrique Conversions se règle ici, dans l'engrenage de la
           * métrique — comme sur le dashboard Google Analytics. Ces quatre propriétés sont
           * ce dont la modale a besoin pour la charger, l'enregistrer et la rendre.
           *
           * `onAdvancedConfigSave` recharge : les séries et tous les tableaux sont calculés
           * côté serveur avec l'ancienne définition. Sans ce rechargement, l'écran afficherait
           * le nouveau nom au-dessus des anciens chiffres — pire qu'un écran inchangé,
           * puisque rien ne le signalerait.
           */
          token={token}
          propertyId={selectedAccountProp?.id || selectedAccount}
          currentAdvancedConfig={conversionConfig}
          onAdvancedConfigSave={() => window.location.reload()}
        />
      )}
    </div>
  );
});

DeviceTable.displayName = 'DeviceTable';

export default DeviceTable;